import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Wrench, Shield, Rocket, Brain, Star, Telescope, Code2, BarChart3,
  Coins, Check, Trash2, Sparkles
} from "lucide-react";
import SpeakerButton from "./SpeakerButton";

const ICONS = {
  wrench: Wrench, shield: Shield, rocket: Rocket, brain: Brain,
  star: Star, telescope: Telescope, code: Code2, chart: BarChart3,
};

const COLOR_STYLES = {
  gold: {
    pill: "bg-yellow-300 text-black",
    iconWrap: "bg-gradient-to-br from-yellow-300 to-yellow-600 text-black eigoo-glow-gold",
    accent: "text-yellow-300",
  },
  red: {
    pill: "bg-red-500 text-white",
    iconWrap: "bg-gradient-to-br from-red-500 to-red-700 text-white eigoo-glow-red",
    accent: "text-red-400",
  },
  white: {
    pill: "bg-white text-black",
    iconWrap: "bg-gradient-to-br from-white to-zinc-300 text-black",
    accent: "text-white",
  },
};

export const MissionCard = ({ mission, onComplete, onDelete }) => {
  const Icon = ICONS[mission.icon] || Wrench;
  const style = COLOR_STYLES[mission.color] || COLOR_STYLES.gold;
  const [completing, setCompleting] = useState(false);

  const completeIt = async () => {
    if (mission.completed || completing) return;
    setCompleting(true);
    await onComplete(mission.id);
    setCompleting(false);
  };

  const sayText = `${mission.title}. ${mission.summary}. Reward: ${mission.coins} Academy Coins and ${mission.xp} experience.`;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className={`relative eigoo-glass rounded-3xl p-5 md:p-6 transition-all ${mission.completed ? "opacity-60" : ""}`}
      data-testid={`mission-card-${mission.id}`}
    >
      {mission.source === "voice" && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-300/10 border border-yellow-300/25 text-yellow-200 text-[10px] font-display font-bold tracking-widest">
          <Sparkles size={10} /> VOICE
        </div>
      )}
      <div className="flex items-start gap-4">
        <div className={`h-16 w-16 rounded-2xl flex items-center justify-center shrink-0 ${style.iconWrap}`}>
          <Icon size={32} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-xl md:text-2xl leading-tight">{mission.title}</h3>
            <SpeakerButton text={sayText} testid={`speaker-mission-${mission.id}`} size="sm" />
          </div>
          {mission.summary && (
            <p className="text-white/70 text-sm md:text-base mt-1 leading-relaxed">{mission.summary}</p>
          )}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-display font-bold ${style.pill}`}>
              <Coins size={14} /> {mission.coins}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-display font-bold bg-white/10 border border-white/15 text-white">
              <Star size={14} /> {mission.xp} XP
            </span>
            {mission.completed && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-display font-bold bg-green-500/15 text-green-300 border border-green-500/30">
                <Check size={14} /> DONE
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-5">
        <button
          onClick={completeIt}
          disabled={mission.completed || completing}
          className={`btn-eigoo flex-1 ${mission.completed ? "bg-green-500/20 text-green-300 cursor-default" : "btn-gold"}`}
          data-testid={`complete-mission-${mission.id}`}
        >
          <Check size={20} />
          {mission.completed ? "Completed" : completing ? "Saving..." : "Complete"}
        </button>
        <button
          onClick={() => onDelete(mission.id)}
          aria-label="Delete mission"
          className="btn-eigoo bg-white/5 hover:bg-red-500/20 hover:text-red-300 border border-white/10 px-4"
          data-testid={`delete-mission-${mission.id}`}
        >
          <Trash2 size={18} />
        </button>
      </div>
    </motion.div>
  );
};

export default MissionCard;
